import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft,
  Loader2,
  Sparkles,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Building2,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/payroll/new")({
  component: NewPayrollRun,
});

type Profile = { id: string; full_name: string | null; email: string };
type PayType = "hourly" | "daily";
type Rate = { user_id: string; hourly_rate: number; pay_type: PayType };

type SiteBucket = {
  job_site_id: string | null;
  job_site_name: string;
  project_id: string | null;
  project_name: string | null;
  hours: number;
  entries_count: number;
  entry_ids: string[];
  days: Set<string>;
};

type EmployeeRow = {
  user_id: string;
  name: string;
  email: string;
  hourly_rate: number;
  pay_type: PayType;
  hours: number;
  days_worked: number;
  entries_count: number;
  sites: SiteBucket[];
  gross: number;
};

type PendingInfo = { pending: number; rejected: number };

function toLocalISO(d: Date) {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return toLocalISO(d);
}

function startOfWeekISO(offsetWeeks = 0) {
  const d = new Date();
  const day = d.getDay();
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff + offsetWeeks * 7);
  return toLocalISO(d);
}

function endOfWeekISO(offsetWeeks = 0) {
  const start = new Date(startOfWeekISO(offsetWeeks));
  start.setDate(start.getDate() + 6);
  return toLocalISO(start);
}

function NewPayrollRun() {
  const { t } = useTranslation();
  const { company, profile, hasRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasRole("admin");

  const [name, setName] = useState("");
  const [periodStart, setPeriodStart] = useState(todayISO(-13));
  const [periodEnd, setPeriodEnd] = useState(todayISO(0));
  // Default pay date = period end + 1 day (user can still override manually)
  // Use UTC math to avoid timezone shifting the date by ±1 day
  const addDaysISO = (iso: string, days: number) => {
    const [y, m, d] = iso.split("-").map(Number);
    const utc = Date.UTC(y, m - 1, d) + days * 86400000;
    const dt = new Date(utc);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };
  const [payDate, setPayDate] = useState(addDaysISO(todayISO(0), 1));
  const [payDateTouched, setPayDateTouched] = useState(false);

  // Auto-sync payDate to periodEnd + 1 day until the user edits it manually
  useEffect(() => {
    if (!payDateTouched && periodEnd) {
      setPayDate(addDaysISO(periodEnd, 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodEnd]);
  const [notes, setNotes] = useState("");
  // One paystub per employee. Per-site detail is preserved in entry_links
  // so the cash-flow and admin views can still break things down by job site.

  const [rows, setRows] = useState<EmployeeRow[]>([]);
  const [pendingInfo, setPendingInfo] = useState<PendingInfo>({ pending: 0, rejected: 0 });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      toast.error(t("pages.payroll.new_page.errors.adminOnly"));
      navigate({ to: "/payroll" });
    }
  }, [isAdmin, navigate, t]);

  const setPreset = (preset: "last_week" | "this_week" | "last_biweek" | "last_month") => {
    if (preset === "last_week") {
      setPeriodStart(startOfWeekISO(-1));
      setPeriodEnd(endOfWeekISO(-1));
    } else if (preset === "this_week") {
      setPeriodStart(startOfWeekISO(0));
      setPeriodEnd(endOfWeekISO(0));
    } else if (preset === "last_biweek") {
      setPeriodStart(startOfWeekISO(-2));
      setPeriodEnd(endOfWeekISO(-1));
    } else if (preset === "last_month") {
      const d = new Date();
      const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const last = new Date(d.getFullYear(), d.getMonth(), 0);
      setPeriodStart(toLocalISO(first));
      setPeriodEnd(toLocalISO(last));
    }
  };

  const loadPreview = async () => {
    if (!company?.id) return;
    if (!periodStart || !periodEnd || periodEnd < periodStart) {
      toast.error(t("pages.payroll.new_page.errors.invalidRange"));
      return;
    }
    setLoadingPreview(true);

    const startTs = new Date(periodStart + "T00:00:00").toISOString();
    const endTs = new Date(periodEnd + "T23:59:59").toISOString();

    const { data: allEntries, error } = await supabase
      .from("time_entries")
      .select("id, user_id, hours, status, clock_in, clock_out, project_id, job_site_id")
      .eq("company_id", company.id)
      .not("clock_out", "is", null)
      .gte("clock_in", startTs)
      .lte("clock_in", endTs);

    if (error) {
      toast.error(error.message);
      setLoadingPreview(false);
      return;
    }

    const entries = allEntries ?? [];
    const candidateApproved = entries.filter((e) => e.status === "approved");

    // Exclude entries already linked to a previous payroll run.
    // Without this filter, employees whose hours were fully paid still appear
    // in the preview, and inserting items for them only succeeds for the row
    // — the entry_links insert fails on the UNIQUE(time_entry_id) constraint,
    // leaving phantom paystubs with no hours.
    let approved = candidateApproved;
    if (candidateApproved.length > 0) {
      const candidateIds = candidateApproved.map((e) => e.id as string);
      const { data: alreadyPaid } = await supabase
        .from("payroll_run_entry_links")
        .select("time_entry_id")
        .in("time_entry_id", candidateIds);
      const paidSet = new Set((alreadyPaid ?? []).map((p: any) => p.time_entry_id as string));
      approved = candidateApproved.filter((e) => !paidSet.has(e.id as string));
    }
    setPendingInfo({
      pending: entries.filter((e) => e.status === "pending").length,
      rejected: entries.filter((e) => e.status === "rejected").length,
    });

    type Agg = { hours: number; count: number; entry_ids: string[]; days: Set<string> };
    const byUserSite: Record<string, Record<string, Agg>> = {};
    const dayKey = (iso: string) => {
      // Use the local date portion of the clock_in to determine "1 day worked".
      const d = new Date(iso);
      return toLocalISO(d);
    };
    approved.forEach((e) => {
      const uid = e.user_id as string;
      const sid = (e.job_site_id as string | null) ?? "__nosite__";
      if (!byUserSite[uid]) byUserSite[uid] = {};
      if (!byUserSite[uid][sid]) {
        byUserSite[uid][sid] = { hours: 0, count: 0, entry_ids: [], days: new Set() };
      }
      byUserSite[uid][sid].hours += Number(e.hours || 0);
      byUserSite[uid][sid].count += 1;
      byUserSite[uid][sid].entry_ids.push(e.id as string);
      if (e.clock_in) byUserSite[uid][sid].days.add(dayKey(e.clock_in as string));
    });

    const userIds = Object.keys(byUserSite);
    if (userIds.length === 0) {
      setRows([]);
      setLoadingPreview(false);
      return;
    }

    const siteIds = Array.from(
      new Set(approved.map((e) => e.job_site_id).filter((v): v is string => !!v)),
    );

    const [{ data: profs }, { data: ratesData }, { data: sites }] = await Promise.all([
      supabase.from("profiles").select("id, full_name, email").in("id", userIds),
      supabase
        .from("employee_pay_rates")
        .select("user_id, hourly_rate, pay_type")
        .eq("company_id", company.id)
        .in("user_id", userIds),
      siteIds.length > 0
        ? supabase
            .from("job_sites")
            .select("id, name, project_id, project:projects(name)")
            .in("id", siteIds)
        : Promise.resolve({ data: [] as any[] }),
    ]);

    const profMap: Record<string, Profile> = {};
    (profs ?? []).forEach((p) => (profMap[p.id] = p as Profile));

    const rateMap: Record<string, { rate: number; pay_type: PayType }> = {};
    (ratesData as Rate[] | null)?.forEach((r) => {
      rateMap[r.user_id] = {
        rate: Number(r.hourly_rate),
        pay_type: (r.pay_type ?? "hourly") as PayType,
      };
    });

    const siteMap: Record<
      string,
      { name: string; project_id: string | null; project: string | null }
    > = {};
    (sites ?? []).forEach((s: any) => {
      siteMap[s.id] = {
        name: s.name,
        project_id: s.project_id ?? null,
        project: s.project?.name ?? null,
      };
    });

    const employeeRows: EmployeeRow[] = userIds.map((uid) => {
      const p = profMap[uid];
      const rateInfo = rateMap[uid] ?? { rate: 0, pay_type: "hourly" as PayType };
      const sitesObj = byUserSite[uid];
      const sitesArr: SiteBucket[] = Object.entries(sitesObj).map(([sid, agg]) => ({
        job_site_id: sid === "__nosite__" ? null : sid,
        job_site_name:
          sid === "__nosite__"
            ? t("pages.payroll.new_page.noJobSiteFallback")
            : (siteMap[sid]?.name ?? t("pages.payroll.new_page.removedJobSite")),
        project_id: sid === "__nosite__" ? null : (siteMap[sid]?.project_id ?? null),
        project_name: sid === "__nosite__" ? null : (siteMap[sid]?.project ?? null),
        hours: Math.round(agg.hours * 100) / 100,
        entries_count: agg.count,
        entry_ids: agg.entry_ids,
        days: agg.days,
      }));
      sitesArr.sort((a, b) => a.job_site_name.localeCompare(b.job_site_name));
      const totalHours = Math.round(sitesArr.reduce((s, x) => s + x.hours, 0) * 100) / 100;
      const totalCount = sitesArr.reduce((s, x) => s + x.entries_count, 0);
      // Distinct days across ALL sites for this employee
      const distinctDays = new Set<string>();
      sitesArr.forEach((s) => s.days.forEach((d) => distinctDays.add(d)));
      const daysWorked = distinctDays.size;
      const gross =
        rateInfo.pay_type === "daily"
          ? Math.round(daysWorked * rateInfo.rate * 100) / 100
          : Math.round(totalHours * rateInfo.rate * 100) / 100;
      return {
        user_id: uid,
        name: p?.full_name ?? p?.email ?? "—",
        email: p?.email ?? "",
        hourly_rate: rateInfo.rate,
        pay_type: rateInfo.pay_type,
        hours: totalHours,
        days_worked: daysWorked,
        entries_count: totalCount,
        sites: sitesArr,
        gross,
      };
    });
    employeeRows.sort((a, b) => a.name.localeCompare(b.name));
    setRows(employeeRows);
    setLoadingPreview(false);
  };

  useEffect(() => {
    if (company?.id) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, periodStart, periodEnd]);

  const updateRate = (uid: string, rate: number) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.user_id !== uid) return r;
        const gross =
          r.pay_type === "daily"
            ? Math.round(r.days_worked * rate * 100) / 100
            : Math.round(r.hours * rate * 100) / 100;
        return { ...r, hourly_rate: rate, gross };
      }),
    );
  };

  const toggleExpand = (uid: string) => setExpanded((prev) => ({ ...prev, [uid]: !prev[uid] }));

  const totalHours = useMemo(() => rows.reduce((s, r) => s + r.hours, 0), [rows]);
  const totalGross = useMemo(() => rows.reduce((s, r) => s + r.gross, 0), [rows]);
  const totalSiteItems = useMemo(() => rows.reduce((s, r) => s + r.sites.length, 0), [rows]);

  const handleGenerate = async () => {
    if (!company?.id || !profile?.id) return;
    if (rows.length === 0) {
      toast.error(t("pages.payroll.new_page.errors.nothingToPay"));
      return;
    }

    const missingRate = rows.filter((r) => r.hourly_rate <= 0);
    if (missingRate.length > 0) {
      toast.error(
        t("pages.payroll.new_page.errors.missingRate", {
          names: missingRate.map((r) => r.name).join(", "),
        }),
      );
      return;
    }

    setCreating(true);

    const ratesToSave = rows
      .filter((r) => r.hourly_rate > 0)
      .map((r) => ({
        company_id: company.id,
        user_id: r.user_id,
        hourly_rate: r.hourly_rate,
        pay_type: r.pay_type,
        currency: company.currency,
        created_by: profile.id,
      }));
    if (ratesToSave.length > 0) {
      await (supabase as any)
        .from("employee_pay_rates")
        .upsert(ratesToSave, { onConflict: "company_id,user_id" });
    }

    // Pre-fetch hours for all entries we are about to link, so each per-employee
    // run can populate payroll_run_entry_links with the correct hours/amount.
    const allEntryIds = rows.flatMap((r) => r.sites.flatMap((s) => s.entry_ids));
    const hoursById = new Map<string, number>();
    if (allEntryIds.length > 0) {
      const { data: entriesForLinks } = await supabase
        .from("time_entries")
        .select("id, hours")
        .in("id", allEntryIds);
      (entriesForLinks ?? []).forEach((e: any) => hoursById.set(e.id, Number(e.hours ?? 0)));
    }

    // Create ONE payroll_run per employee, in sequence.
    // Each run holds a single payroll_run_item for that employee.
    const createdRunIds: string[] = [];
    let firstRunId: string | null = null;
    const failures: string[] = [];

    for (const r of rows) {
      const employeeName = r.name;
      const runName = name ? `${name} — ${employeeName}` : employeeName;

      const { data: run, error: runErr } = await (supabase as any)
        .from("payroll_runs")
        .insert({
          company_id: company.id,
          name: runName,
          period_start: periodStart,
          period_end: periodEnd,
          pay_date: payDate || null,
          currency: company.currency,
          notes: notes || null,
          created_by: profile.id,
        })
        .select("id")
        .single();

      if (runErr || !run) {
        failures.push(`${employeeName}: ${runErr?.message ?? "—"}`);
        continue;
      }

      if (!firstRunId) firstRunId = run.id;
      createdRunIds.push(run.id);

      const { data: insertedItem, error: itemErr } = await (supabase as any)
        .from("payroll_run_items")
        .insert({
          payroll_run_id: run.id,
          user_id: r.user_id,
          employee_name: r.name,
          employee_email: r.email,
          hours: r.hours,
          hourly_rate: r.hourly_rate,
          pay_type: r.pay_type,
          days_worked: r.days_worked,
          entries_count: r.entries_count,
          gross_pay: r.gross,
          notes:
            r.sites.length > 1
              ? t("pages.payroll.new_page.aggregatedNote", { count: r.sites.length })
              : null,
        })
        .select("id")
        .single();

      if (itemErr || !insertedItem) {
        failures.push(`${employeeName}: ${itemErr?.message ?? "item insert failed"}`);
        continue;
      }

      // For daily pay, distribute the daily gross across linked entries by hours
      // so per-site amounts still sum to the employee's gross.
      const totalHoursForUser = r.hours || 0;
      const linkRows: any[] = [];
      r.sites.forEach((s) => {
        s.entry_ids.forEach((eid) => {
          const h = hoursById.get(eid) ?? 0;
          const amount =
            r.pay_type === "daily"
              ? totalHoursForUser > 0
                ? Math.round((h / totalHoursForUser) * r.gross * 100) / 100
                : 0
              : Math.round(h * r.hourly_rate * 100) / 100;
          linkRows.push({
            company_id: company.id,
            payroll_run_id: run.id,
            payroll_run_item_id: insertedItem.id,
            time_entry_id: eid,
            user_id: r.user_id,
            job_site_id: s.job_site_id,
            project_id: s.project_id,
            hours: h,
            amount,
          });
        });
      });

      if (linkRows.length > 0) {
        const { error: linksErr } = await (supabase as any)
          .from("payroll_run_entry_links")
          .insert(linkRows);
        if (linksErr) {
          failures.push(`${employeeName}: ${linksErr.message}`);
        }
      }
    }

    if (failures.length > 0) {
      toast.error(
        t("pages.payroll.new_page.errors.linksFailed", {
          defaultValue: "Algumas folhas falharam: {{msg}}",
          msg: failures.join(" | "),
        }),
      );
    }

    if (createdRunIds.length === 0) {
      setCreating(false);
      return;
    }

    toast.success(t("pages.payroll.new_page.success", { count: createdRunIds.length }));
    if (firstRunId) {
      navigate({ to: "/payroll/$runId", params: { runId: firstRunId } });
    } else {
      navigate({ to: "/payroll" });
    }
  };

  const fmtMoney = (v: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: company?.currency ?? "USD",
    }).format(v);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/payroll"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> {t("pages.payroll.new_page.back")}
        </Link>
        <h1 className="mt-2 text-3xl font-bold tracking-tight">
          {t("pages.payroll.new_page.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("pages.payroll.new_page.subtitleAggregate")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("pages.payroll.new_page.periodCard")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">
              {t("pages.payroll.new_page.quickPeriods")}
            </Label>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setPreset("this_week")}>
                {t("pages.payroll.new_page.thisWeek")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPreset("last_week")}>
                {t("pages.payroll.new_page.lastWeek")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPreset("last_biweek")}>
                {t("pages.payroll.new_page.lastBiweek")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPreset("last_month")}>
                {t("pages.payroll.new_page.lastMonth")}
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="start">{t("pages.payroll.new_page.startLabel")}</Label>
              <Input
                id="start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="end">{t("pages.payroll.new_page.endLabel")}</Label>
              <Input
                id="end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="payDate">{t("pages.payroll.new_page.payDateLabel")}</Label>
              <Input
                id="payDate"
                type="date"
                value={payDate}
                onChange={(e) => {
                  setPayDate(e.target.value);
                  setPayDateTouched(true);
                }}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="name">{t("pages.payroll.new_page.nameLabel")}</Label>
            <Input
              id="name"
              placeholder={t("pages.payroll.new_page.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="notes">{t("pages.payroll.new_page.notesLabel")}</Label>
            <Textarea
              id="notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {(pendingInfo.pending > 0 || pendingInfo.rejected > 0) && (
        <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div>
            <p className="font-medium text-destructive">
              {t("pages.payroll.new_page.outOfPeriodTitle")}
            </p>
            <p className="text-muted-foreground">
              <Trans
                i18nKey="pages.payroll.new_page.outOfPeriodDesc"
                count={pendingInfo.pending}
                values={{ pending: pendingInfo.pending, rejected: pendingInfo.rejected }}
                components={{ strong: <strong /> }}
              />
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("pages.payroll.new_page.previewCard")}</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPreview ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />{" "}
              {t("pages.payroll.new_page.calculating")}
            </p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t("pages.payroll.new_page.noApprovedHours")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="w-8 py-2 pr-2"></th>
                    <th className="py-2 pr-2">{t("pages.payroll.new_page.headerEmployee")}</th>
                    <th className="py-2 pr-2 text-right">
                      {t("pages.payroll.new_page.headerJobSites")}
                    </th>
                    <th className="py-2 pr-2 text-right">
                      {t("pages.payroll.new_page.headerHours")}
                    </th>
                    <th className="py-2 pr-2 text-right">
                      {t("pages.payroll.new_page.headerRate")}
                    </th>
                    <th className="py-2 pr-2 text-right">
                      {t("pages.payroll.new_page.headerGross")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => {
                    const isOpen = !!expanded[r.user_id];
                    return (
                      <Fragment key={r.user_id}>
                        <tr>
                          <td className="py-2 pr-2">
                            <button
                              type="button"
                              onClick={() => toggleExpand(r.user_id)}
                              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                              aria-label={t("pages.payroll.new_page.expandLabel")}
                            >
                              {isOpen ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                          <td className="py-2 pr-2">
                            <p className="font-medium">{r.name}</p>
                            <p className="text-xs text-muted-foreground">{r.email}</p>
                          </td>
                          <td className="py-2 pr-2 text-right">
                            <Badge variant="secondary" className="gap-1">
                              <Building2 className="h-3 w-3" />
                              {r.sites.length}
                            </Badge>
                          </td>
                          <td className="py-2 pr-2 text-right tabular-nums">
                            {r.pay_type === "daily" ? (
                              <span>
                                {r.days_worked}{" "}
                                <span className="text-xs text-muted-foreground">
                                  {t("pages.payroll.new_page.daysShort", { defaultValue: "dias" })}
                                </span>
                                <div className="text-[10px] text-muted-foreground">
                                  ({r.hours.toFixed(2)} h)
                                </div>
                              </span>
                            ) : (
                              r.hours.toFixed(2)
                            )}
                          </td>
                          <td className="py-2 pr-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Input
                                type="number"
                                step="0.01"
                                min={0}
                                value={r.hourly_rate}
                                onChange={(e) => updateRate(r.user_id, Number(e.target.value) || 0)}
                                className="ml-auto h-8 w-24 text-right"
                              />
                              <span className="text-xs text-muted-foreground">
                                {r.pay_type === "daily"
                                  ? t("pages.payroll.new_page.perDay", { defaultValue: "/dia" })
                                  : t("pages.payroll.new_page.perHour", { defaultValue: "/h" })}
                              </span>
                            </div>
                          </td>
                          <td className="py-2 pr-2 text-right font-semibold tabular-nums">
                            {fmtMoney(r.gross)}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-muted/30">
                            <td colSpan={6} className="px-4 py-3">
                              <table className="w-full text-xs">
                                <thead className="text-muted-foreground">
                                  <tr>
                                    <th className="py-1 pr-2 text-left">
                                      {t("pages.payroll.new_page.subHeaderJobSite")}
                                    </th>
                                    <th className="py-1 pr-2 text-left">
                                      {t("pages.payroll.new_page.subHeaderProject")}
                                    </th>
                                    <th className="py-1 pr-2 text-right">
                                      {t("pages.payroll.new_page.subHeaderEntries")}
                                    </th>
                                    <th className="py-1 pr-2 text-right">
                                      {t("pages.payroll.new_page.subHeaderHours")}
                                    </th>
                                    <th className="py-1 pr-2 text-right">
                                      {t("pages.payroll.new_page.subHeaderGross")}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.sites.map((s) => (
                                    <tr
                                      key={s.job_site_id ?? "nosite"}
                                      className="border-t border-border/50"
                                    >
                                      <td className="py-1 pr-2 font-medium">{s.job_site_name}</td>
                                      <td className="py-1 pr-2 text-muted-foreground">
                                        {s.project_name ?? "—"}
                                      </td>
                                      <td className="py-1 pr-2 text-right tabular-nums">
                                        {s.entries_count}
                                      </td>
                                      <td className="py-1 pr-2 text-right tabular-nums">
                                        {s.hours.toFixed(2)}
                                      </td>
                                      <td className="py-1 pr-2 text-right tabular-nums">
                                        {fmtMoney(
                                          r.pay_type === "daily"
                                            ? r.hours > 0
                                              ? Math.round((s.hours / r.hours) * r.gross * 100) /
                                                100
                                              : 0
                                            : Math.round(s.hours * r.hourly_rate * 100) / 100,
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="border-t border-border font-semibold">
                  <tr>
                    <td colSpan={2} className="py-2 pr-2">
                      {t("pages.payroll.new_page.totalRow", { count: rows.length })}
                    </td>
                    <td />
                    <td className="py-2 pr-2 text-right tabular-nums">{totalHours.toFixed(2)}</td>
                    <td />
                    <td className="py-2 pr-2 text-right tabular-nums">{fmtMoney(totalGross)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate({ to: "/payroll" })}>
          {t("pages.payroll.new_page.cancel")}
        </Button>
        <Button onClick={handleGenerate} disabled={creating || rows.length === 0} className="gap-2">
          {creating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {t("pages.payroll.new_page.generate")}
        </Button>
      </div>
    </div>
  );
}
